import { Chart, ArcElement, Legend, Tooltip } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { IGetSummaryPaymentTypeChart } from 'features/report/services/types/reportStaff';
import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';
import { useContainerSize } from '../hooks/useContainerSize';
import { formatCurrency } from 'utils/formatCurrency';
Chart.register(ArcElement);
interface PaymentTypesChartProps {
  data?: IGetSummaryPaymentTypeChart[];
}

const mapPaymentDataForChart = (apiData: IGetSummaryPaymentTypeChart[]) => {
  const colorMap: Record<string, string> = {
    CASH: '#4384FF',
    CREDIT_CARD: '#4B63C7',
    GIFT_CARD: '#3AADD9',
    LOYALTY_POINT: '#EBA33B',
    CHECK: '#F6D465',
    OTHERS: '#9747FF',
  };

  const nameMap: Record<string, string> = {
    CASH: 'Cash',
    CREDIT_CARD: 'Card',
    GIFT_CARD: 'Gift Card',
    LOYALTY_POINT: 'Loyalty Reward',
    CHECK: 'Check',
    OTHERS: 'Other',
  };

  const total = apiData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return [
      { name: 'Cash', value: 0, percentage: 0, color: colorMap['CASH'] },
      { name: 'Card', value: 0, percentage: 0, color: colorMap['CREDIT_CARD'] },
      {
        name: 'Gift Card',
        value: 0,
        percentage: 0,
        color: colorMap['GIFT_CARD'],
      },
      {
        name: 'Loyalty Reward',
        value: 0,
        percentage: 0,
        color: colorMap['LOYALTY_POINT'],
      },
      { name: 'Check', value: 0, percentage: 0, color: colorMap['CHECK'] },
      { name: 'Other', value: 0, percentage: 0, color: colorMap['OTHERS'] },
    ];
  }

  return apiData.map((item) => {
    const percentage = Math.round((item.value / total) * 100);

    return {
      name: nameMap[item.key] || item.key,
      value: item.value,
      percentage,
      color: colorMap[item.key] || '#a855f7',
    };
  });
};

const PaymentTypesChart: React.FC<PaymentTypesChartProps> = ({ data }) => {
  // Use our custom hook
  const [containerRef, containerSize] = useContainerSize<HTMLDivElement>();

  const chartData = mapPaymentDataForChart(data || []);
  const allZeros = chartData.every((item) => item.value === 0);
  const isSmallScreen = containerSize.width < 500;

  const displayData = useMemo(
    () => ({
      labels: chartData.map((item) => item.name),
      datasets: [
        {
          data: allZeros
            ? chartData.map(() => 1)
            : chartData.map((item) => item.value),
          backgroundColor: chartData.map((item) => item.color),
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    }),
    [chartData, allZeros]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      aspectRatio: containerSize.width < 768 ? 1 : 1.2,
      cutout: '60%',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: !allZeros,
          titleFont: {
            size: isSmallScreen ? 16 : 18,
            family: 'Poppins',
            weight: 'bold',
          },
          bodyFont: {
            size: isSmallScreen ? 14 : 16,
            family: 'Poppins',
          },
          padding: 12,
          callbacks: {
            title: (tooltipItems: any) => {
              const item = chartData[tooltipItems[0].dataIndex];
              return `${item.name} (${item.percentage}%)`;
            },
            label: (tooltipItem: any) => {
              const item = chartData[tooltipItem.dataIndex];
              return `${formatCurrency(item.value)}`;
            },
          },
        },
        datalabels: {
          display: !allZeros,
          color: '#fff',
          font: {
            weight: 'bold',
            size: 16,
          },
          formatter: (value: any, context: any) => {
            const item = chartData[context.dataIndex];
            return item.percentage > 0 ? `${item.percentage}%` : '';
          },
        },
      },
    }),
    [chartData, allZeros, isSmallScreen, containerSize.width]
  );

  return (
    <ChartContainer ref={containerRef}>
      <ChartTitle>Payment Types</ChartTitle>

      <ChartWrapper>
        <Doughnut
          data={displayData}
          //@ts-ignore
          options={options}
          plugins={[Tooltip, Legend, ChartDataLabels]}
        />
        {allZeros && <ZeroDataOverlay>Not Available</ZeroDataOverlay>}
      </ChartWrapper>

      <LegendContainer>
        {chartData.map((entry, index) => (
          <LegendItem key={`legend-item-${index}`}>
            <LegendName>
              <ColorIndicator color={entry.color} />
              <span>
                {entry.name} ({entry.percentage}%)
              </span>
            </LegendName>
            <LegendValue>{formatCurrency(entry.value)}</LegendValue>
          </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};

export default PaymentTypesChart;

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartTitle = styled.h2`
  font-size: 26px;
  color: #1e293b;
  font-weight: 400;
  margin: 0 0 24px 0;
  text-transform: capitalize;

  @media (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 16px;
  }
`;

const ChartWrapper = styled.div`
  height: 240px;
  width: 100%;
  max-height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  @media (max-width: 768px) {
    height: 180px;
  }

  @media (max-width: 576px) {
    height: 160px;
  }
`;

const LegendContainer = styled.div`
  margin-top: 16px;
  overflow-y: auto;
  flex: 1;
`;

const LegendItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;

  @media (max-width: 576px) {
    padding: 6px 0;
  }
`;

const LegendName = styled.div`
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #1e293b;
  font-size: 18px;
  text-transform: capitalize;

  @media (max-width: 576px) {
    font-size: 16px;
  }
`;

const ColorIndicator = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${(props) => props.color};

  @media (max-width: 576px) {
    width: 10px;
    height: 10px;
    margin-right: 6px;
  }
`;

const LegendValue = styled.span`
  font-weight: bold;
  color: #1e293b;
  font-size: 18px;

  @media (max-width: 576px) {
    font-size: 16px;
  }
`;

const ZeroDataOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 18px;
  font-weight: 500;
  color: #64748b;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 4px;
  pointer-events: none;
`;
